import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StateService } from '@uirouter/core';

import { map, Observable, tap } from 'rxjs';

import { ConfirmationService } from 'primeng/api';

import { Diagnostic, HistoriqueRubriqueDiagnostic } from '@app/diagnostic/diagnostic.model';
import { Nomenclature } from '@app/nomenclature/nomenclature.model';
import { UtilisateurSite } from '@app/utilisateur/utilisateur.model';
import { EventManagerService, IEventListener } from '@global/event-manager.service';
import { Site } from './site.model';

import { Cache } from '@app/_helpers/cache';
import { prepareQueryParams, prepareQueryParamsForDownload } from '@app/_helpers/prepare-query-params';
import { CodeEvaluation, StatutRecolement, TexteEvaluation } from '@app/texte/texte.model';
import { clone, convertDateFieldsToDate, convertDateFieldsToString, ExtensibleObject, startOfDay, uid } from '@helpers/utils';


interface Sites {
	total: number;
	sites: Site[]
}

interface HistoriqueDiagnostic {
	total: number;
	historique: HistoriqueRubriqueDiagnostic[]
}

export interface StatistiquesConformite {
	total: StatistiquesConformiteGlobales,
	sites: StatistiquesConformiteSite[]
}

export interface StatistiquesConformiteGlobales {
	taux_recolement: number;
	taux_conformite: number;

	nombre_textes: number;
	nombre_textes_statues: number;
    textes_par_statut: Record<StatutRecolement, {nombre: number, proportion: number}>;
    textes_par_etat_evaluation: Record<CodeEvaluation, {nombre: number, proportion: number}>;

	nombre_articles: number;
    articles_par_statut: Record<StatutRecolement, {nombre: number, proportion: number}>;
    articles_par_etat_evaluation: Record<CodeEvaluation, {nombre: number, proportion: number}>;
}

export interface StatistiquesConformiteSite extends StatistiquesConformiteGlobales {
	sit_id: number;
	sit_libelle: string;
}

@Injectable({ providedIn: 'root' })
export class SiteService implements IEventListener {

	private _uuid: string = uid();
	get uuid(): string { return this._uuid; }

	private sitesCacheByAboId = new Map<number, Cache<Sites>>();

	constructor(
		private confirmationService: ConfirmationService,
		private eventManager: EventManagerService,
		private http: HttpClient,
		private stateService: StateService,
	) {
		this.eventManager.registerEvent('logout', this, (args: any) => {
			this.sitesCacheByAboId.clear();
		});
	}

	ngOnDestroy(): void {
	}

	public prepareSiteFromServer(site: Site) {
		let tmp: Site = clone(site);
		convertDateFieldsToDate(tmp, true);
		return tmp;
	}

	public prepareSiteForServer(site: Site) {
		let tmp = structuredClone(site) as ExtensibleObject;
		convertDateFieldsToString(tmp);
		return tmp;
	}

	public prepareSitesFromServer(sites: Site[]) {
		for (let i = 0; i < sites.length ; i++) {
			sites[i] = this.prepareSiteFromServer(sites[i]);
		}
		return sites;
	}

	public getCacheSites(abo_id: number) {
		let cache = this.sitesCacheByAboId.get(abo_id)
		if (cache == undefined) {
			cache = new Cache()
			this.sitesCacheByAboId.set(abo_id, cache);
		}
		if (cache.isNotEmpty()) {
			return cache.currentAsObservable();
		}
		return this.getSites(abo_id)
		.pipe(map((sites: Sites) => {
			cache.update(sites);
			return sites;
		}));
	}

	public getSites(abo_id: number, params: unknown = {}) {
		const tmpParams = prepareQueryParams(params);

		return this.http.get<any>(`/abonnes/${abo_id}/sites`, tmpParams).pipe(
			map(({ sites, total }) => {
				let liste = this.prepareSitesFromServer(sites);
				return {sites: liste, total: total || 0 as number};
			})
		)
	}

	public getCacheSite(abo_id: number, sit_id: number) {
		return this.getCacheSites(abo_id)
		.pipe(map((sites) => {
			let site = sites.sites.find(site => site.sit_id == sit_id);
			if (site != undefined) {
				return site;
			}
			return this.getSite(abo_id, sit_id);
		}));
	}

	public getSite(abo_id: number, sit_id: number) {
		return this.http.get<any>(`/abonnes/${abo_id}/sites/${sit_id}`).pipe(
			map(response => this.prepareSiteFromServer(response))
		)
	}

	public createSite(abo_id: number, site: Site){
		this.sitesCacheByAboId.get(abo_id)?.invalidate();
		let body = this.prepareSiteForServer(site);

		return this.http.post<any>(`/abonnes/${abo_id}/sites`, body);
	}

	public updateSite(abo_id: number, site: Site){
		this.sitesCacheByAboId.get(abo_id)?.invalidate();
		let body = this.prepareSiteForServer(site);

		return this.http.put<any>(`/abonnes/${abo_id}/sites/${site.sit_id}`, body);
	}

	public deleteSite(abo_id: number, sit_id: number){
		this.sitesCacheByAboId.get(abo_id)?.invalidate();

		return this.http.delete<any>(`/abonnes/${abo_id}/sites/${sit_id}`);
	}

	// Utilisateur

	public linkUserToSite(user_id: number, abo_id: number, sit_id: number, nac_id: number){
		this.sitesCacheByAboId.get(abo_id)?.invalidate();

		return this.http.post<any>(`/abonnes/${abo_id}/sites/${sit_id}/utilisateurs/${user_id}`, {
			nac_id: nac_id,
		});
	}

	public unlinkUserFromSite(user_id: number, abo_id: number, sit_id: number){
		this.sitesCacheByAboId.get(abo_id)?.invalidate();

		return this.http.delete<any>(`/abonnes/${abo_id}/sites/${sit_id}/utilisateurs/${user_id}`);
	}

	public editUtilisateurSite(user_id: number, abo_id: number, sit_id: number, utiSite: UtilisateurSite){
		this.sitesCacheByAboId.get(abo_id)?.invalidate();

		return this.http.put<any>(`/abonnes/${abo_id}/sites/${sit_id}/utilisateurs/${user_id}`, {
			nac_id: utiSite.nac_id,
		});
	}

	// Diagnostic

	public getDiagnostic(abo_id: number, sit_id: number) {
		return this.http.get<Diagnostic>(`/abonnes/${abo_id}/sites/${sit_id}/diagnostic`);
	}

	public getHistoriqueDiagnostic(abo_id: number, sit_id: number, params: ExtensibleObject = {}) {
		const tmpParams = prepareQueryParams(params);

		return this.http.get<HistoriqueDiagnostic>(`/abonnes/${abo_id}/sites/${sit_id}/diagnostic/historique`, tmpParams)
		.pipe(map((historique: HistoriqueDiagnostic) => {

			return {
				total: historique.total,
				historique: historique.historique.map((rubrique: any) => {
					let tmp: HistoriqueRubriqueDiagnostic = clone(rubrique);
					convertDateFieldsToDate(tmp, true);
					return tmp;
				})
			};
		}));
	}

	public updateDiagnostic(abo_id: number, sit_id: number, rub_ids: number[]) {
		return this.http.put(`/abonnes/${abo_id}/sites/${sit_id}/diagnostic`, rub_ids);
	}

	public duplicateDiagnostic(sit_id_source: number, abo_id_destination: number, sit_id_destination: number) {
		return this.http.put<{partial: boolean}>(`/abonnes/${abo_id_destination}/sites/${sit_id_destination}/diagnostic`, {sit_id_source: sit_id_source});
	}

	public exportDiagnostic(abo_id: number, sit_id: number) {
		let tmpParams = prepareQueryParamsForDownload({});
		return this.http.get(`/abonnes/${abo_id}/sites/${sit_id}/diagnostic/export`, tmpParams);
	}

	public exportHistoriqueDiagnostic(abo_id: number, sit_id: number, params: ExtensibleObject = {}) {
		let tmpParams = prepareQueryParamsForDownload(params);
		return this.http.get(`/abonnes/${abo_id}/sites/${sit_id}/diagnostic/historique/export`, tmpParams);
	}

	public exportTextesDiagnostic(abo_id: number, sit_id: number, params: ExtensibleObject = {}) {
		const tmpParams = prepareQueryParamsForDownload(params);
		return this.http.get(`/abonnes/${abo_id}/sites/${sit_id}/diagnostic/textes/export`, tmpParams);
	}

	public getMergedNomenclatureDiagnostics(abo_id: number, sit_ids: number[]): Observable<Nomenclature> {
		return this.http.get<Nomenclature>(`/abonnes/${abo_id}/nomenclature_diagnostics_combinee?sit_ids=${sit_ids.join(',')}`);
	}

	public exportHistoriqueRecolementsArticles(abo_id: number, art_id: number, params: any = {}) {
		const tmpParams = prepareQueryParamsForDownload(params);

		return this.http.get(`/abonnes/${abo_id}/articles/${art_id}/recolements/historique/export`, tmpParams);
	}

	// Evaluations

	public prepareTexteEvaluationFromServer(texteEvaluation: TexteEvaluation): TexteEvaluation {
		let tmp = clone(texteEvaluation);
		convertDateFieldsToDate(tmp, true);

		let now = startOfDay(new Date())
		// On ignore les dates de réévaluation dans le passé
		for(let evaluation of tmp.conformite){
			if (evaluation.ctx_date_reevaluation < now) {
				delete evaluation.ctx_date_reevaluation
			}
		}
		for (let article of tmp.articles) {
			for(let evaluation of article.conformite){
				if (evaluation.car_date_reevaluation < now) {
					delete evaluation.car_date_reevaluation
				}
			}
		}
		return tmp;
	}

	public prepareTexteEvaluationForServer(texteEvaluation: TexteEvaluation) {
		let tmp = structuredClone(texteEvaluation) as ExtensibleObject;
		convertDateFieldsToString(tmp);
		return tmp;
	}

	public getEvaluationsTextes(abo_id: number, sit_ids: number[], params: ExtensibleObject = {}) {
		const tmpParams = prepareQueryParams(params);

		return this.http.get<{total: number, textes: TexteEvaluation[] }>(`/abonnes/${abo_id}/conformite_textes?sit_ids=${sit_ids.join(',')}`, tmpParams)
		.pipe(map(({total, textes}) => {
			return {
				total: total,
				textes: textes.map(this.prepareTexteEvaluationFromServer)
			};
		}));
	}

	public getEvaluationTexte(abo_id: number, sit_ids: number[], txt_id: number) {
		return this.http.get(`/abonnes/${abo_id}/textes/${txt_id}/conformite?sit_ids=${sit_ids.join(',')}`)
		.pipe(map((data: any) => this.prepareTexteEvaluationFromServer(data)));
	}

	public exportEvaluationsTextes(abo_id: number, sit_ids: number[], params: unknown = {}) {
		const tmpParams = prepareQueryParamsForDownload(params);

		return this.http.get(`/abonnes/${abo_id}/conformite_textes/export?sit_ids=${sit_ids.join(',')}`, tmpParams);
	}

	public updateSomeEvaluationsTextes(abo_id: number, sit_ids: number[], textes: TexteEvaluation[]) {
		let body: any = textes.map(this.prepareTexteEvaluationForServer);
		return this.http.patch(`/abonnes/${abo_id}/conformite_textes?sit_ids=${sit_ids.join(',')}`, body);
	}

	public getHistoriqueEvaluationsTexte(abo_id: number, txt_id: number, params: any = {}) {
		const tmpParams = prepareQueryParams(params);

		return this.http.get<HistoriqueDiagnostic>(`/abonnes/${abo_id}/textes/${txt_id}/conformite/historique`, tmpParams)
		.pipe(map((historique: any) => {

			return {
				total: historique.total,
				historique: historique.historique.map((rubrique: any) => {
					let tmp = clone(rubrique);
					convertDateFieldsToDate(tmp, true);
					return tmp;
				})
			};
		}));
	}

	public exportHistoriqueEvaluationsTexte(abo_id: number,  txt_id: number, params: any = {}) {
		const tmpParams = prepareQueryParamsForDownload(params);

		return this.http.get(`/abonnes/${abo_id}/textes/${txt_id}/conformite/historique/export`, tmpParams);
	}

	public getHistoriqueEvaluationsArticle(abo_id: number, art_id: number, params: any = {}) {
		const tmpParams = prepareQueryParams(params);

		return this.http.get<HistoriqueDiagnostic>(`/abonnes/${abo_id}/articles/${art_id}/conformite/historique`, tmpParams)
		.pipe(map((historique: any) => {

			return {
				total: historique.total,
				historique: historique.historique.map((rubrique: any) => {
					let tmp = clone(rubrique);
					convertDateFieldsToDate(tmp, true);
					return tmp;
				})
			};
		}));
	}

	public exportHistoriqueEvaluationsArticles(abo_id: number, art_id: number, params: any = {}) {
		const tmpParams = prepareQueryParamsForDownload(params);

		return this.http.get(`/abonnes/${abo_id}/articles/${art_id}/conformite/historique/export`, tmpParams);
	}

	// Statistiques

	public getStatsConformite(abo_id: number, fam_id?: number, thm_id?: number) {
		let urlParams = '';
		if (fam_id) {
			urlParams += 'fam_id=' + fam_id;
		}
		else if (thm_id) {
			urlParams += 'thm_id=' + thm_id
		}
		return this.http.get<StatistiquesConformite | null>(`/abonnes/${abo_id}/statistiques_conformite?${urlParams}`);
	}
}